import { shallowEqual, useSelector } from 'react-redux'

import { isMultiItemArray, tr } from 'mmfintech-commons'

import BackButton from '@components/BackButton'

import { CustomInput } from '@components/CustomInput'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { MethodButton, MethodIcon } from '@components'
import { MethodSelectionContainer, VoucherTitle } from './Blixcard.styled'

import type { CountryChoice, PaymentMethod, PaymentOptionResponse } from 'mmfintech-checkout-commons/types'

type BlixcardMethodStepProps = {
  countryCode: string
  setCountryCode: (code: string) => void
  hasCountrySelected: boolean
  paymentMethod: string
  setPaymentMethod: (value: string) => void
  filteredMethods: PaymentOptionResponse[]
  handleBack: () => void
  handleNext: () => void
  skipVoucherOptionSelection?: boolean
}

export const BlixcardMethodStep = (props: BlixcardMethodStepProps) => {
  const {
    countryCode,
    setCountryCode,
    hasCountrySelected,
    paymentMethod,
    setPaymentMethod,
    filteredMethods,
    handleBack,
    handleNext,
    skipVoucherOptionSelection
  } = props

  const { checkoutCountries, checkoutPayError, checkoutPayFetching, sessionError } = useSelector(
    ({ checkout: { checkoutCountries, checkoutPayError, checkoutPayFetching, sessionError } }) => ({
      checkoutCountries,
      checkoutPayError,
      checkoutPayFetching,
      sessionError
    }),
    shallowEqual
  )

  return (
    <>
      {typeof handleBack === 'function' ? (
        <div className='mb-2'>
          <BackButton onClick={handleBack} />
        </div>
      ) : null}

      <VoucherTitle data-test='select-method-page'>{tr('CHECKOUT.VOUCHER.METHOD.TITLE', 'Select method')}</VoucherTitle>

      <ErrorDisplay error={[sessionError, checkoutPayError]} />

      <div>
        {isMultiItemArray(checkoutCountries) && (!hasCountrySelected || skipVoucherOptionSelection) ? (
          <CustomInput
            type='select'
            name='countryCode'
            data-test='country-select'
            label={tr('CHECKOUT.PAYMENT.COUNTRY_PLACEHOLDER', 'Select country')}
            options={checkoutCountries?.map((country: CountryChoice) => ({
              value: country.countryCode,
              label: country.name
            }))}
            value={countryCode}
            onChange={(_name: string, value: string) => setCountryCode(value)}
          />
        ) : null}

        <MethodSelectionContainer>
          {filteredMethods.map((method: PaymentOptionResponse) => {
            const selected = method.paymentMethod === paymentMethod

            return (
              <MethodButton
                key={method.paymentMethod}
                data-test={`method-${method.paymentMethod}`}
                icon={<MethodIcon method={method.paymentMethod as PaymentMethod} />}
                caption={tr(`METADATA.PAYMENT_OPTIONS.${method.paymentMethod}`, method.paymentMethod)}
                loading={selected && checkoutPayFetching}
                selected={selected}
                onClick={() => (selected ? handleNext() : setPaymentMethod(method.paymentMethod))}
              />
            )
          })}
        </MethodSelectionContainer>
      </div>
    </>
  )
}
